/**
 * ReseauStatistiquesPage — Visibilité & conversion (Lot R4).
 * Gratuit : chiffres bruts 30 j. Premium : provenance, entonnoir, zones, séries.
 * Aperçu premium avec vraies données floutées (jamais de fausse promesse).
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BarChart3, Lock, Loader2, TrendingUp, MapPin, ArrowRight } from 'lucide-react';
import api from '../../lib/api';
import ReseauLayout from '../../components/reseau/ReseauLayout';

function StatTile({ label, value, testid }) {
    return (
        <div className="reseau-card p-4" data-testid={testid}>
            <p className="text-3xl font-extrabold tabular-nums" style={{ color: 'var(--reseau-ink)' }}>
                {value ?? 0}
            </p>
            <p className="text-xs mt-1 leading-tight" style={{ color: 'var(--reseau-ink-muted)' }}>
                {label}
            </p>
        </div>
    );
}

function PremiumTeaser({ onUpgrade }) {
    return (
        <div className="reseau-card p-6 relative overflow-hidden" data-testid="stats-premium-preview">
            <div className="space-y-3 select-none" style={{ filter: 'blur(6px)', opacity: 0.55 }} aria-hidden="true">
                {[70, 45, 90, 30].map((w, i) => (
                    <div key={i} className="h-6 rounded-lg"
                         style={{ width: `${w}%`, background: 'var(--reseau-violet-soft)' }} />
                ))}
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <span className="w-10 h-10 rounded-full flex items-center justify-center mb-3"
                      style={{ background: 'var(--reseau-violet-soft)' }}>
                    <Lock className="w-5 h-5" style={{ color: 'var(--reseau-violet)' }} />
                </span>
                <p className="text-sm font-bold mb-1" style={{ color: 'var(--reseau-ink)' }}>
                    D'où viennent vos visiteurs ? Où perdez-vous vos opportunités ?
                </p>
                <p className="text-xs mb-4" style={{ color: 'var(--reseau-ink-muted)' }}>
                    Provenance, entonnoir de conversion, zones de visibilité et rapport mensuel —
                    avec Homly Réseau Premium, 19 € HT/mois.
                </p>
                <button onClick={onUpgrade}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white"
                        style={{ background: 'var(--reseau-violet)' }}
                        data-testid="stats-upgrade-btn">
                    Découvrir Premium <ArrowRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

function Funnel({ funnel }) {
    const max = Math.max(1, ...funnel.map((f) => f.value));
    return (
        <div className="reseau-card p-5" data-testid="stats-funnel">
            <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider mb-4"
                style={{ color: 'var(--reseau-ink-muted)' }}>
                <TrendingUp className="w-4 h-4" style={{ color: 'var(--reseau-violet)' }} />
                Mon entonnoir (30 j)
            </h2>
            <div className="space-y-3">
                {funnel.map((f) => (
                    <div key={f.step}>
                        <div className="flex justify-between text-xs mb-1">
                            <span style={{ color: 'var(--reseau-ink)' }}>{f.step}</span>
                            <span className="font-bold tabular-nums" style={{ color: 'var(--reseau-ink)' }}>{f.value}</span>
                        </div>
                        <div className="h-2.5 rounded-full" style={{ background: 'var(--reseau-bg-muted)' }}>
                            <div className="h-2.5 rounded-full transition-all"
                                 style={{ width: `${Math.max(3, (f.value / max) * 100)}%`, background: 'var(--reseau-violet)' }} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function ReseauStatistiquesPage() {
    const [user, setUser] = useState(null);
    const [overview, setOverview] = useState(null);
    const [detailed, setDetailed] = useState(null);
    const [isPremium, setIsPremium] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const [me, ov] = await Promise.all([api.get('/auth/me'), api.get('/reseau/stats/overview')]);
                setUser(me.data);
                setOverview(ov.data);
                try {
                    const det = await api.get('/reseau/stats/detailed');
                    setDetailed(det.data);
                    setIsPremium(true);
                } catch (e) {
                    if (e?.response?.status !== 403) throw e;
                }
            } catch (e) {
                if (e?.response?.status === 401) navigate('/login?next=/reseau/statistiques');
            } finally {
                setLoading(false);
            }
        })();
    }, [navigate]);

    return (
        <ReseauLayout user={user} headerTitle="Statistiques">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="reseau-stats-page">
                <header>
                    <h1 className="flex items-center gap-2 text-xl font-bold" style={{ color: 'var(--reseau-ink)' }}>
                        <BarChart3 className="w-5 h-5" style={{ color: 'var(--reseau-violet)' }} />
                        Ma visibilité
                    </h1>
                    <p className="text-sm" style={{ color: 'var(--reseau-ink-muted)' }}>
                        Les 30 derniers jours. Vous êtes comparé à vous-même, jamais aux autres.
                    </p>
                </header>

                {loading && (
                    <div className="flex justify-center py-16">
                        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--reseau-violet)' }} />
                    </div>
                )}

                {!loading && overview && (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <StatTile label="Vues du profil" value={overview.profile_views} testid="stat-views" />
                            <StatTile label="Apparitions en recherche" value={overview.search_appearances} testid="stat-appearances" />
                            <StatTile label="Clics vers un contact" value={overview.contact_clicks} testid="stat-clicks" />
                            <StatTile label="Demandes vendeurs" value={overview.seller_intents} testid="stat-intents" />
                        </div>

                        {!isPremium && (
                            <PremiumTeaser onUpgrade={() => navigate('/reseau/parametres#premium')} />
                        )}

                        {isPremium && detailed && (
                            <>
                                <Funnel funnel={detailed.funnel} />

                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div className="reseau-card p-5" data-testid="stats-provenance">
                                        <h2 className="text-sm font-semibold uppercase tracking-wider mb-4"
                                            style={{ color: 'var(--reseau-ink-muted)' }}>
                                            D'où viennent mes demandes
                                        </h2>
                                        {detailed.provenance.length === 0 ? (
                                            <p className="text-sm" style={{ color: 'var(--reseau-ink-muted)' }}>
                                                Pas encore de demande sur la période.
                                            </p>
                                        ) : (
                                            <ul className="space-y-2">
                                                {detailed.provenance.map((p) => (
                                                    <li key={p.source} className="flex justify-between text-sm">
                                                        <span style={{ color: 'var(--reseau-ink)' }}>{p.label}</span>
                                                        <span className="font-bold tabular-nums"
                                                              style={{ color: 'var(--reseau-violet)' }}>{p.count}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    <div className="reseau-card p-5" data-testid="stats-zones">
                                        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider mb-4"
                                            style={{ color: 'var(--reseau-ink-muted)' }}>
                                            <MapPin className="w-4 h-4" style={{ color: 'var(--reseau-violet)' }} />
                                            Zones de mes visiteurs
                                        </h2>
                                        {detailed.zones.length === 0 ? (
                                            <p className="text-sm" style={{ color: 'var(--reseau-ink-muted)' }}>
                                                Pas encore assez de données de recherche.
                                            </p>
                                        ) : (
                                            <ul className="space-y-2">
                                                {detailed.zones.map((z) => (
                                                    <li key={z.city} className="flex justify-between text-sm">
                                                        <span style={{ color: 'var(--reseau-ink)' }}>{z.city}</span>
                                                        <span className="font-bold tabular-nums"
                                                              style={{ color: 'var(--reseau-violet)' }}>{z.count}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        <p className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>
                            Confidentialité : la provenance est toujours synthétique et groupée —
                            jamais la navigation individuelle d'un vendeur.{' '}
                            <Link to="/reseau/parametres" className="underline">Gérer ma visibilité</Link>
                        </p>
                    </>
                )}
            </div>
        </ReseauLayout>
    );
}
